import { useEffect } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import './ChatSidebar.css';
import { useChatSidebar } from './useChatSidebar';
import { Icon } from '../../modules/icon';
import { Session } from '../../api/gateways/ChatGateway';
import { formatDate, formatSessionDate } from './utils/formatDate';
import { SessionRename } from './SessionRename';
import { ConfirmModal } from '../../components/confirm-modal';

interface ChatSidebarProps {
	onSessionClick?: (sessionId: string) => void;
	currentSessionId?: string | null;
	onRefreshReady?: (refreshFn: () => Promise<void>) => void;
	onClearConversation?: () => void;
	hasMessages?: boolean;
}

export function ChatSidebar({
	onSessionClick,
	currentSessionId,
	onRefreshReady,
	onClearConversation,
	hasMessages = false,
}: ChatSidebarProps) {
	const {
		isExpanded,
		toggleSidebar,
		sessions,
		isLoading,
		totalMessages,
		totalTokens,
		sessionsCount,
		editingSessionId,
		editValue,
		isRenaming,
		startRename,
		cancelRename,
		saveRename,
		setEditValue,
		showDeleteModal,
		closeDeleteModal,
		handleDeleteSession,
		openDeleteModal,
		refreshSessions,
	} = useChatSidebar(onClearConversation);

	// Expose refreshSessions to parent through onRefreshReady callback
	useEffect(() => {
		if (onRefreshReady) {
			onRefreshReady(refreshSessions);
		}
	}, [onRefreshReady, refreshSessions]);

	const handleSessionClick = (session: Session) => {
		if (onSessionClick) {
			onSessionClick(session.sessionId);
		}
	};

	const getSessionTitle = (session: Session): string => {
		if (session.conversationName) {
			return session.conversationName;
		}
		return formatSessionDate(session.startedAt);
	};

	return (
		<>
			{/* Floating button for mobile */}
			<Button
				variant='primary'
				className='chat-sidebar-floating-btn'
				onClick={toggleSidebar}
				aria-label='Open chat sessions'
			>
				<Icon type='fa-solid fa-comments' className='chat-floating-icon' />
				{sessionsCount > 0 && (
					<span className='chat-floating-badge'>{sessionsCount}</span>
				)}
			</Button>

			{/* Backdrop overlay for mobile */}
			<div
				className={`chat-sidebar-backdrop ${
					isExpanded ? 'backdrop-visible' : ''
				}`}
				onClick={toggleSidebar}
				aria-hidden='true'
			/>

			<div className={`chat-sidebar ${isExpanded ? 'expanded' : 'collapsed'}`}>
				<div className='chat-sidebar-header'>
					<Button
						variant='outline-secondary'
						size='sm'
						onClick={toggleSidebar}
						className='chat-sidebar-toggle'
						aria-label={isExpanded ? 'Smanji sidebar' : 'Proširi sidebar'}
					>
						<span className='chat-sidebar-icon-wrapper'>
							{isExpanded ? (
								<Icon
									type='fa-solid fa-chevron-left'
									className='chat-sidebar-icon icon-expanded'
								/>
							) : (
								<>
									<Icon
										type='fa-solid fa-comments'
										className='chat-sidebar-icon icon-collapsed'
									/>
									{sessionsCount > 0 && (
										<span className='chat-badge-toggle'>{sessionsCount}</span>
									)}
								</>
							)}
						</span>
					</Button>
				</div>
				<div
					className={`chat-sidebar-content-wrapper ${
						isExpanded ? 'content-visible' : 'content-hidden'
					}`}
				>
					<div className='chat-sidebar-content'>
						<div className='chat-sidebar-title'>
							<div className='d-flex justify-content-between align-items-center mb-2'>
								<h6 className='mb-0'>
									<Icon type='fa-solid fa-comments' className='me-2' />
									Chat Sessions
								</h6>
								{onClearConversation && (
									<Button
										variant='outline-secondary'
										size='sm'
										onClick={(e) => {
											e.stopPropagation();
											onClearConversation();
										}}
										disabled={!hasMessages}
										className='chat-sidebar-clear-btn'
										aria-label='Clear conversation'
									>
										<Icon type='fa-solid fa-plus' className='me-1' />
										New chat session
									</Button>
								)}
							</div>
							{sessionsCount > 0 && (
								<div className='chat-sidebar-stats'>
									<small className='text-muted'>
										{sessionsCount} sessions • {totalMessages} messages •{' '}
										{totalTokens.toLocaleString()} total tokens
									</small>
								</div>
							)}
						</div>

						{isLoading ? (
							<div className='chat-sidebar-loading'>
								<Spinner animation='border' size='sm' variant='primary' />
								<small className='text-muted ms-2'>Loading...</small>
							</div>
						) : sessions.length === 0 ? (
							<div className='chat-sidebar-empty'>
								<Icon type='fa-solid fa-inbox' className='empty-icon' />
								<p className='text-muted small mb-0'>No previous sessions</p>
							</div>
						) : (
							<div className='chat-sessions-list'>
								{sessions.map((session: Session) => (
									<div
										key={session.sessionId}
										className={`chat-session-item ${
											currentSessionId === session.sessionId ? 'active' : ''
										}`}
										onClick={() => handleSessionClick(session)}
									>
										<div className='chat-session-header'>
											<div className='chat-session-info'>
												{editingSessionId === session.sessionId ? (
													<SessionRename
														sessionId={session.sessionId}
														currentName={getSessionTitle(session)}
														isEditing={true}
														editValue={editValue}
														isRenaming={isRenaming}
														onEditValueChange={setEditValue}
														onSave={saveRename}
														onCancel={cancelRename}
														onEditClick={(e) => {
															e.stopPropagation();
															startRename(
																session.sessionId,
																getSessionTitle(session)
															);
														}}
													/>
												) : (
													<>
														<SessionRename
															sessionId={session.sessionId}
															currentName={getSessionTitle(session)}
															isEditing={false}
															editValue={''}
															isRenaming={false}
															onEditValueChange={() => {}}
															onSave={() => {}}
															onCancel={() => {}}
															onEditClick={(e) => {
																e.stopPropagation();
																startRename(
																	session.sessionId,
																	getSessionTitle(session)
																);
															}}
														/>
														<div className='chat-session-time'>
															{formatDate(session.lastActivityAt)}
														</div>
													</>
												)}
											</div>
											<Button
												variant='link'
												size='sm'
												className='chat-session-delete-btn'
												onClick={(e) => {
													e.stopPropagation();
													openDeleteModal(session.sessionId);
												}}
												aria-label='Delete session'
											>
												<Icon type='fa-solid fa-trash' />
											</Button>
										</div>
										<div className='chat-session-details'>
											<div className='chat-session-stat'>
												<Icon
													type='fa-solid fa-message'
													className='stat-icon'
												/>
												<span>{session.messageCount} messages</span>
											</div>
											{session.totalTokens > 0 && (
												<div className='chat-session-stat'>
													<Icon
														type='fa-solid fa-coins'
														className='stat-icon'
													/>
													<span>
														{session.totalTokens.toLocaleString()} tokens
													</span>
												</div>
											)}
										</div>
									</div>
								))}
							</div>
						)}
					</div>
				</div>
			</div>
			<ConfirmModal
				show={showDeleteModal}
				onCancel={closeDeleteModal}
				onDismiss={handleDeleteSession}
				title='Delete Session'
				body='Are you sure you want to delete this session? This action cannot be undone.'
			/>
		</>
	);
}
